import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiOpenInNew, mdiPaperclip, mdiPencilOutline } from "@mdi/js";
import { Button, Chip, Drawer, Popover, Spinner } from "@heroui/react";
import { resolveUploadUrl } from "@pro-sto/shared";
import { OpenAPI } from "@pro-sto/api-client";
import { feedbackStore } from "@/stores/FeedbackStore";
import type { FeedbackStatus } from "@/stores/FeedbackStore";
import { ROUTES } from "@/constants/routes";
import {
  FEEDBACK_STATUS_COLOR,
  FEEDBACK_STATUS_LABEL,
  formatFeedbackDate,
  getFeedbackClientPhone,
  getFeedbackStatusActions,
} from "./constants";
import FeedbackCommentFooter from "./FeedbackCommentFooter";
import FeedbackTimeline from "./FeedbackTimeline";
import "./FeedbackDrawer.css";

function getFileName(path: string): string {
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

const FeedbackDrawer: React.FC<{ onClose: () => void }> = observer(
  ({ onClose }) => {
    const navigate = useNavigate();
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const {
      isDrawerOpen,
      selectedFeedback,
      isDetailLoading,
      isStatusUpdating,
    } = feedbackStore;

    const handleClose = () => onClose();

    const handleStatusChange = (status: FeedbackStatus) => {
      setIsStatusMenuOpen(false);
      void feedbackStore.updateStatus(status);
    };

    const handleOpenClient = () => {
      if (!selectedFeedback?.client_uuid) return;
      handleClose();
      navigate(`${ROUTES.CLIENTS}/${selectedFeedback.client_uuid}`);
    };

    const statusActions = selectedFeedback
      ? getFeedbackStatusActions(selectedFeedback.status)
      : [];

    const clientPhone = selectedFeedback
      ? getFeedbackClientPhone(selectedFeedback)
      : null;

    const clientContactItems = selectedFeedback
      ? [
          selectedFeedback.name ? { value: selectedFeedback.name } : null,
          clientPhone ? { value: clientPhone, href: `tel:${clientPhone}` } : null,
          selectedFeedback.email
            ? {
                value: selectedFeedback.email,
                href: `mailto:${selectedFeedback.email}`,
              }
            : null,
        ].filter(Boolean) as Array<{ value: string; href?: string }>
      : [];

    useEffect(() => {
      if (!isDrawerOpen) setIsStatusMenuOpen(false);
    }, [isDrawerOpen]);

    return (
      <Drawer.Backdrop
        isOpen={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        className="fb-drawer-backdrop"
      >
        <Drawer.Content placement="right" className="fb-drawer-content">
          <Drawer.Dialog className="fb-drawer-dialog">
            <Drawer.CloseTrigger />
            <Drawer.Header className="fb-drawer-header">
              {isDetailLoading ? (
                <Drawer.Heading>Загрузка...</Drawer.Heading>
              ) : (
                <>
                  <div className="fb-drawer-header__top">
                    <Drawer.Heading>
                      {selectedFeedback?.topic_name ?? "Обращение"}
                    </Drawer.Heading>
                    {selectedFeedback && (
                      <div className="fb-drawer-header__status">
                        <Chip
                          size="sm"
                          color={FEEDBACK_STATUS_COLOR[selectedFeedback.status]}
                          variant="soft"
                        >
                          {FEEDBACK_STATUS_LABEL[selectedFeedback.status]}
                        </Chip>
                        {statusActions.length > 0 && (
                          <Popover
                            isOpen={isStatusMenuOpen}
                            onOpenChange={setIsStatusMenuOpen}
                          >
                            <Popover.Trigger>
                              <Button
                                isIconOnly
                                variant="ghost"
                                size="sm"
                                aria-label="Изменить статус"
                                isDisabled={isStatusUpdating}
                                className="fb-drawer-status-edit"
                              >
                                <Icon path={mdiPencilOutline} size={0.75} />
                              </Button>
                            </Popover.Trigger>
                            <Popover.Content
                              placement="bottom end"
                              className="fb-status-menu-popover"
                            >
                              <Popover.Dialog className="fb-status-menu">
                                {statusActions.map((action) => (
                                  <button
                                    key={action.status}
                                    type="button"
                                    className="fb-status-menu__item"
                                    disabled={isStatusUpdating}
                                    onClick={() =>
                                      handleStatusChange(action.status)
                                    }
                                  >
                                    {action.label}
                                  </button>
                                ))}
                              </Popover.Dialog>
                            </Popover.Content>
                          </Popover>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedFeedback && (
                    <>
                      <p className="fb-drawer-subtitle">
                        {formatFeedbackDate(selectedFeedback.created_at)}
                      </p>

                      <p className="fb-drawer-header__message">
                        {selectedFeedback.message}
                      </p>

                      {selectedFeedback.files &&
                        selectedFeedback.files.length > 0 && (
                          <ul className="fb-drawer-header__files">
                            {selectedFeedback.files.map((file) => (
                              <li key={file}>
                                <a
                                  href={resolveUploadUrl(
                                    file,
                                    import.meta.env.VITE_BASE_STATIC_PATH ||
                                      OpenAPI.BASE ||
                                      "",
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="fb-drawer-file-link"
                                >
                                  <Icon path={mdiPaperclip} size={0.65} />
                                  {getFileName(file)}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}

                      {selectedFeedback.client_uuid &&
                        clientContactItems.length > 0 && (
                          <div className="fb-drawer-header__client">
                            <div className="fb-drawer-header__client-info">
                              {clientContactItems.map((item, index) => (
                                <span
                                  key={item.value}
                                  className="fb-drawer-header__client-group"
                                >
                                  {index > 0 && (
                                    <span
                                      className="fb-drawer-header__client-sep"
                                      aria-hidden="true"
                                    >
                                      ·
                                    </span>
                                  )}
                                  {item.href ? (
                                    <a
                                      href={item.href}
                                      className="fb-drawer-link fb-drawer-header__client-item"
                                    >
                                      {item.value}
                                    </a>
                                  ) : (
                                    <span className="fb-drawer-header__client-item">
                                      {item.value}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="fb-drawer-header__client-btn"
                              onPress={handleOpenClient}
                            >
                              <Icon path={mdiOpenInNew} size={0.75} />
                              Открыть карточку клиента
                            </Button>
                          </div>
                        )}
                    </>
                  )}
                </>
              )}
            </Drawer.Header>

            <Drawer.Body className="fb-drawer-body">
              {isDetailLoading ? (
                <div className="fb-drawer-loading">
                  <Spinner size="lg" />
                </div>
              ) : selectedFeedback ? (
                <FeedbackTimeline />
              ) : null}
            </Drawer.Body>

            {!isDetailLoading && selectedFeedback && (
              <Drawer.Footer className="fb-drawer-footer">
                <FeedbackCommentFooter />
              </Drawer.Footer>
            )}
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    );
  },
);

FeedbackDrawer.displayName = "FeedbackDrawer";
export default FeedbackDrawer;
